import { useEffect, useState } from "react";
import { Button } from "@fluentui/react-components";
import * as microsoftTeams from "@microsoft/teams-js";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks/hooks";
import { userFound } from "redux-oidc-2";
import { getUserManager } from "../redux/store";
import { User } from "oidc-client-ts";
import { useSearchParams } from "react-router-dom";
import { baseApi } from "../redux/api/baseApi";
import { useTabConfig } from "../components/hooks/useTabConfig";
import { setTenantId } from "../redux/slices/revSlice";
import { jwtDecode } from "jwt-decode";
import { PartialSpinny } from "../components/Spinny";
import SilentTest from "./SilentTest";

export const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [params] = useSearchParams();
    const tenantUidFromParams = params.get("revTenantId") || "";
    const user = useReduxSelector((state) => state.auth.user);
    const dispatch = useReduxDispatch();
    const tabConfig = useTabConfig();
    const [debugMsg, setDebugMsg] = useState<string>("");
    //const revTenantId = useReduxSelector((state) => state.rev.tenantUid);
    const [silentSignIn, setSilentSignIn] = useState<boolean>(false);
    //the tenantId saved in a cookie
    const cookieTenantId = document.cookie.split(";").find(cookie => cookie.trim().startsWith("revTenantId="))?.split("=")[1];
    //a function to set the tenant ID in a cookie (used by the auth callback page to persist the tenant ID across the redirect)
    const setTenantIdCookie = (tenantId: string) => {
        document.cookie = `revTenantId=${tenantId}; secure=true; path=/; max-age=31536000; SameSite=None`; // Expires in 1 year
    }


    //Sets the tenant ID in the redux state from the URL param if present (used for multi-tenant support to ensure we use the correct OIDC settings and user manager instance)
    //useEffect(() => {
    //if (tenantUidFromParams) {
    //    dispatch(setTenantId(tenantUidFromParams));
    //only do this after login to facilkitate silent sign in on iOS
    // console.log("cookieTenantId now:", cookieTenantId, document.cookie.split(";"));
    // setTenantIdCookie(tenantUidFromParams);
    // console.log("setTenantIdCookie set:", tenantUidFromParams, window.location.origin);
    //  }
    //}, [tenantUidFromParams]);

    useEffect(() => {
        console.log("AuthWrapper mounted, checking for existing user...", user, tabConfig, tabConfig?.tabName === "debug");
        setDebugMsg((prev) => prev + "AuthWrapper mounted, checking for existing user: " + JSON.stringify(user) + "\n\n");
    }, [user]);

    //IOS / Mobile fix - ensure we check for a stored user on mount and restore it to redux state if found
    useEffect(() => {
        microsoftTeams.app.initialize().catch(console.error);

        const restoreUser = async () => {
            try {
                const storedUser = await getUserManager(cookieTenantId).getUser();

                console.log("Restoring user from storage", storedUser);

                if (storedUser) {
                    console.log("User found in storage", storedUser);
                    setDebugMsg((prev) => prev + "Restoring user from storage: " + JSON.stringify(storedUser) + "\n\n");
                    dispatch(userFound(storedUser));

                    // Optional but smart:
                    dispatch(baseApi.util.resetApiState());
                } else if (cookieTenantId) {
                    console.log("No stored user found by has cookie. Trying silent signin...", 'nom nom=', cookieTenantId);
                    setDebugMsg((prev) => prev + "No stored user found\n\n");
                    setSilentSignIn(true);

                    getUserManager(cookieTenantId).signinSilent().then((silentUser) => {
                        console.log("Silent signin successful, user:", silentUser);
                        setDebugMsg((prev) => prev + "Silent signin successful: " + JSON.stringify(silentUser) + "\n\n");
                        setSilentSignIn(false);
                        dispatch(userFound(silentUser!));
                    }).catch((err) => {
                        console.error("Silent signin failed:", err);
                        setDebugMsg((prev) => prev + "Silent signin failed: " + JSON.stringify(err) + "\n\n");
                        setSilentSignIn(false);
                    });
                } else {
                    console.log("No stored user found and no tenant ID in params, cannot restore user.", 'tenantUidFromParams=', tenantUidFromParams, 'cookieTenantId=', cookieTenantId);
                }
            } catch (err) {
                console.error("Failed to restore user", err);
                setDebugMsg((prev) => prev + "Failed to restore user: " + JSON.stringify(err) + "\n\n");
            }
        };

        restoreUser();
    }, [dispatch]);



    const login = () => {
        console.log("Starting login process...");

        microsoftTeams.authentication.authenticate({
            url: `${window.location.origin}/signin-start?revTenantId=${cookieTenantId || ''}`,
            width: 800,
            height: 1000
        }).then(async (result) => {
            // result here is whatever you pass to notifySuccess in your callback page
            console.log("Auth successful, result:", result);
            //setToken(JSON.parse(result) as User)


            //Nope, this only works when the tenantID is in the TAB config which its NOT for personal tabs. Rather decode the Access_Token and get the tenantID from CLAIMS
            // if (tenantUidFromParams) {
            //     console.log("cookieTenantId now:", cookieTenantId, document.cookie.split(";"));
            //     setTenantIdCookie(tenantUidFromParams);
            //     console.log("setTenantIdCookie set:", tenantUidFromParams, window.location.origin);
            // }


            // Persist in oidc-client-ts storage so redux-oidc-2 can manage it
            // Convert the result into an oidc-client-ts User instance (handles both serialized string or object)
            try {
                let userToStore;
                if (typeof result === "string") {
                    userToStore = User.fromStorageString(result);
                } else {
                    userToStore = new User(result as any);
                }

                console.log('userToStore:', userToStore, 'tenantUidFromParams:', tenantUidFromParams);

                //Store the users Rev Tenant ID in a Cookie for iOS silent sign in (localstorage or session storage isn't reliably persisted across app sessions on iOS, but cookies are)
                //const myToken = jwt.jwtDecode((userToStore as User).access_token);
                const myToken = jwtDecode<Record<string, unknown>>((userToStore as User).access_token);
                if (myToken["tenantUId"] && typeof myToken["tenantUId"] === "string") {
                    console.log("cookieTenantId now:", cookieTenantId, document.cookie.split(";"));
                    setTenantIdCookie(myToken["tenantUId"]);
                    console.log("setTenantIdCookie set:", myToken["tenantUId"], window.location.origin, document.cookie.split(";"));
                }

                await getUserManager().storeUser(userToStore);
                dispatch(userFound(userToStore));

                //Rest the API state to clear any cached data that might be stale due to the new user context
                dispatch(baseApi.util.resetApiState());

            } catch (err) {
                console.error("Failed to store user:", err);
            }
        }).catch((reason) => {
            console.error("Auth failed:", reason);
        });

    };

    // if (tabConfig?.tabName === "silent") {
    //     return <SilentTest />
    // }

    if (tabConfig?.tabName === "debug") {
        return <div className="">
            <h1>DEBUG</h1>
            {debugMsg.split("\n").map((line, idx) => <p key={idx}>{line}</p>)}
            <p>cookieTenantId: {cookieTenantId}</p>
            <div className="d-flex p-3 vh-100 align-items-start justify-content-center"><Button onClick={login} className="ms-auto me-auto"><img src="/images/rev-logo-96.png" alt="Revelation logo" style={{ height: 24, marginRight: 8 }} />Sign in with Revelation helpdesk</Button></div>;
        </div>;
    }

    if (silentSignIn) {
        return <PartialSpinny small={true} text={`Attempting silent sign-in...`} showCancel={true} canceled={() => setSilentSignIn(false)} />;
    }

    if (!user?.access_token) {
        return <div className="d-flex p-3 vh-100 align-items-start justify-content-center"><Button onClick={login} className="ms-auto me-auto"><img src="/images/rev-logo-96.png" alt="Revelation logo" style={{ height: 24, marginRight: 8 }} />Sign in with Revelation helpdesk</Button></div>;
    }
    return <>{children}</>;
}
